import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "painting-web-design-colorado",
  metaTitle: "Painting Contractor Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom painting web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Painting contractor marketing", to: "/painting-marketing-colorado" },
  ],
  currentLabel: "Painting Contractor Web Design Colorado",
  eyebrow: "Painting Contractor Web Design — Colorado",
  h1: (
    <>
      Painting Contractor <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded painting websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Painting websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first painting sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Painting Contractor web design in Colorado",
    body: "Painting Contractor web design is the practice of building conversion-focused, mobile-first websites for painting companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with portfolio galleries that don't kill Core Web Vitals." },
    { title: "Service pages per job type", body: "Interior, exterior, cabinet refinishing, deck staining, commercial, HOA — each ranks independently." },
    { title: "Before/after galleries", body: "Lazy-loaded portfolio with image schema for SEO." },
    { title: "Color consultation booking", body: "Free color consultation CTAs that convert 2–3x higher than generic quote forms." },
    { title: "Service area pages", body: "Dedicated city pages with proper LocalBusiness schema." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Visual proof is the close", body: "Painting is a portfolio sale — before/after galleries above the fold, not on a buried subpage." },
    { heading: "Cabinet refinishing is the high-margin niche", body: "Dedicated cabinet refinishing pages capture a search niche most painters ignore." },
    { heading: "Interior vs exterior are different seasons", body: "Seasonal SEO works only if interior and exterior have their own landing pages." },
  ],
  relatedLinks: [
    { to: "/painting-marketing-colorado", label: "Painting contractor marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado painting case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a painting website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a painting website?", a: "Typical timeline is 4–6 weeks for a 12–20 page painting site with full service and service-area pages. Larger multi-location painting companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs painting companies?", a: "No — we build painting sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Painting Contractor Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for painting companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/painting-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
